import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, MessageSquare, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NotesTimeline = ({ entityType, entityId, entityName }) => {
  const [timeline, setTimeline] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [noteContent, setNoteContent] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    if (entityId) {
      fetchTimeline();
      fetchNotes();
    }
  }, [entityId]);

  const fetchTimeline = async () => {
    try {
      const response = await axios.get(
        `${API}/notes/entity/${entityType}/${entityId}/timeline`,
        { withCredentials: true }
      );
      setTimeline(response.data);
    } catch (error) {
      console.error('Error fetching timeline:', error);
    }
  };

  const fetchNotes = async () => {
    try {
      const response = await axios.get(`${API}/notes`, {
        params: { entity_type: entityType, entity_id: entityId },
        withCredentials: true,
      });
      setNotes(response.data);
    } catch (error) {
      console.error('Error fetching notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!noteContent.trim()) {
      toast({
        title: "Error",
        description: "Note content cannot be empty",
        variant: "destructive",
      });
      return;
    }

    try {
      if (editingNote) {
        // Update existing note
        await axios.put(
          `${API}/notes/${editingNote.id}`,
          { content: noteContent },
          { withCredentials: true }
        );
        toast({
          title: "Note Updated",
          description: "Your note has been updated successfully.",
        });
      } else {
        // Create new note
        await axios.post(
          `${API}/notes`,
          {
            entity_type: entityType,
            entity_id: entityId,
            entity_name: entityName,
            content: noteContent,
          },
          { withCredentials: true }
        );
        toast({
          title: "Note Added",
          description: "Your note has been added successfully.",
        });
      }

      setNoteContent('');
      setEditingNote(null);
      setIsDialogOpen(false);
      fetchTimeline();
      fetchNotes();
    } catch (error) {
      console.error('Error saving note:', error);
      toast({
        title: "Error",
        description: "Failed to save note. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (note) => {
    setEditingNote(note);
    setNoteContent(note.content);
    setIsDialogOpen(true);
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) {
      return;
    }

    try {
      await axios.delete(`${API}/notes/${noteId}`, { withCredentials: true });
      toast({
        title: "Note Deleted",
        description: "The note has been deleted successfully.",
      });
      fetchTimeline();
      fetchNotes();
    } catch (error) {
      console.error('Error deleting note:', error);
      toast({
        title: "Error",
        description: "Failed to delete note. Please try again.",
        variant: "destructive",
      });
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getActivityIcon = (activityType) => {
    const icons = {
      note_created: '📝',
      contact_created: '👤',
      company_created: '🏢',
      deal_created: '💰',
      campaign_created: '📧',
      task_created: '✅',
    };
    return icons[activityType] || '📌';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0176D3]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Quick Add Note */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Notes & Timeline
            </CardTitle>
            <Dialog open={isDialogOpen} onOpenChange={(open) => {
              setIsDialogOpen(open);
              if (!open) {
                setEditingNote(null);
                setNoteContent('');
              }
            }}>
              <DialogTrigger asChild>
                <Button className="bg-[#0176D3] hover:bg-[#0155A8]" size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Note
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                  <DialogTitle>{editingNote ? 'Edit Note' : 'Add New Note'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="content">Note Content</Label>
                    <textarea
                      id="content"
                      value={noteContent}
                      onChange={(e) => setNoteContent(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md min-h-[150px]"
                      placeholder="Add your note here..."
                      required
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setIsDialogOpen(false);
                        setEditingNote(null);
                        setNoteContent('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-[#0176D3] hover:bg-[#0155A8]">
                      {editingNote ? 'Update Note' : 'Add Note'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {timeline.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p>No notes or activities yet. Add your first note!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {timeline.map((item, index) => (
                <div key={`${item.type}-${item.id}-${index}`} className="flex gap-4">
                  {/* Timeline dot */}
                  <div className="flex flex-col items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${
                      item.type === 'note' 
                        ? 'bg-[#0176D3] text-white' 
                        : 'bg-gray-200 text-gray-600'
                    }`}>
                      {item.type === 'note' ? '📝' : getActivityIcon(item.activity_type)}
                    </div>
                    {index < timeline.length - 1 && (
                      <div className="w-0.5 h-full bg-gray-200 flex-1 mt-2" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 pb-8">
                    <div className={`p-4 rounded-lg ${
                      item.type === 'note' 
                        ? 'bg-orange-50 border border-orange-200' 
                        : 'bg-gray-50 border border-gray-200'
                    }`}>
                      {item.type === 'note' ? (
                        <div>
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Clock className="w-4 h-4" />
                              <span>{formatDate(item.created_at)}</span>
                              {item.updated_at && item.updated_at !== item.created_at && (
                                <span className="text-gray-500">(edited)</span>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const note = notes.find(n => n.id === item.id);
                                  if (note) handleEdit(note);
                                }}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(item.id)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          <p className="text-gray-900 whitespace-pre-wrap">{item.content}</p>
                        </div>
                      ) : (
                        <div>
                          <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                            <Clock className="w-4 h-4" />
                            <span>{formatDate(item.created_at)}</span>
                          </div>
                          <p className="text-gray-700">{item.description}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default NotesTimeline;
